// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import {
	getArrayDiff,
	getObjectKeys,
	NullPartial,
	omitKeys,
	pickKeys,
	subsetTypeHasKey,
	ValueOf,
} from "@hex-insights/core";
import { StudentNoteCreateInput, StudentNoteDetailQuery, StudentNoteUpdateInput } from "../../../Utilities";
import { MediaAttachmentFormConversion } from "../MediaAttachment";
import { SharingRecordFormConversion } from "../SharingRecord";
import { StudentAcademicNoteFormConversion } from "../StudentAcademicNote";
import { StudentAttendanceNoteFormConversion } from "../StudentAttendanceNote";
import { StudentBehaviorNoteFormConversion } from "../StudentBehaviorNote";
import { StudentHealthNoteFormConversion } from "../StudentHealthNote";
import { StudentIncidentNoteFormConversion } from "../StudentIncidentNote";
import { StudentInjuryNoteFormConversion } from "../StudentInjuryNote";
import { StudentMedicationNoteFormConversion } from "../StudentMedicationNote";
import { StudentSocialEmotionalNoteFormConversion } from "../StudentSocialEmotionalNote";
import * as StudentNoteFormValues from "./form-values";

/**
 * Converts the given `formValues` for the create form to a GraphQL create input for the StudentNote model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInput<K extends keyof StudentNoteFormValues.Create = keyof StudentNoteFormValues.Create>(
	formValues: StudentNoteFormValues.CreateSubset<K>,
): StudentNoteCreateInput {
	const inputFormValues = omitKeys(formValues, [
		"mediaAttachments",
		"sharingRecord",
		"studentAcademicNote",
		"studentAttendanceNote",
		"studentBehaviorNote",
		"studentHealthNote",
		"studentIncidentNote",
		"studentInjuryNote",
		"studentMedicationNote",
		"studentSocialEmotionalNote",
	]);
	const input: NullPartial<StudentNoteCreateInput> = {
		...inputFormValues,
		mediaAttachments: subsetTypeHasKey(formValues, "mediaAttachments")
			? formValues.mediaAttachments.map((e) => MediaAttachmentFormConversion.toGQLCreateInStudentNoteInput(e))
			: undefined,
		sharingRecord:
			subsetTypeHasKey(formValues, "sharingRecord") && formValues.sharingRecord.length > 0
				? SharingRecordFormConversion.toGQLCreateInStudentNoteInput(formValues.sharingRecord[0])
				: undefined,
		studentAcademicNote:
			subsetTypeHasKey(formValues, "studentAcademicNote") && formValues.studentAcademicNote.length > 0
				? StudentAcademicNoteFormConversion.toGQLCreateInStudentNoteInput(formValues.studentAcademicNote[0])
				: undefined,
		studentAttendanceNote:
			subsetTypeHasKey(formValues, "studentAttendanceNote") && formValues.studentAttendanceNote.length > 0
				? StudentAttendanceNoteFormConversion.toGQLCreateInStudentNoteInput(formValues.studentAttendanceNote[0])
				: undefined,
		studentBehaviorNote:
			subsetTypeHasKey(formValues, "studentBehaviorNote") && formValues.studentBehaviorNote.length > 0
				? StudentBehaviorNoteFormConversion.toGQLCreateInStudentNoteInput(formValues.studentBehaviorNote[0])
				: undefined,
		studentHealthNote:
			subsetTypeHasKey(formValues, "studentHealthNote") && formValues.studentHealthNote.length > 0
				? StudentHealthNoteFormConversion.toGQLCreateInStudentNoteInput(formValues.studentHealthNote[0])
				: undefined,
		studentIncidentNote:
			subsetTypeHasKey(formValues, "studentIncidentNote") && formValues.studentIncidentNote.length > 0
				? StudentIncidentNoteFormConversion.toGQLCreateInStudentNoteInput(formValues.studentIncidentNote[0])
				: undefined,
		studentInjuryNote:
			subsetTypeHasKey(formValues, "studentInjuryNote") && formValues.studentInjuryNote.length > 0
				? StudentInjuryNoteFormConversion.toGQLCreateInStudentNoteInput(formValues.studentInjuryNote[0])
				: undefined,
		studentMedicationNote:
			subsetTypeHasKey(formValues, "studentMedicationNote") && formValues.studentMedicationNote.length > 0
				? StudentMedicationNoteFormConversion.toGQLCreateInStudentNoteInput(formValues.studentMedicationNote[0])
				: undefined,
		studentSocialEmotionalNote:
			subsetTypeHasKey(formValues, "studentSocialEmotionalNote") && formValues.studentSocialEmotionalNote.length > 0
				? StudentSocialEmotionalNoteFormConversion.toGQLCreateInStudentNoteInput(
						formValues.studentSocialEmotionalNote[0],
				  )
				: undefined,
	};
	return input as StudentNoteCreateInput;
}

/**
 * Converts the given `formValues` for the detail form to a GraphQL update input for the StudentNote model.
 *
 * @param formValues The form values to convert. Generally only the changed form values.
 * @param initialFormValues The initial values of the form.
 */
export function toGQLUpdateInput<K extends keyof StudentNoteFormValues.Detail = keyof StudentNoteFormValues.Detail>(
	formValues: Partial<StudentNoteFormValues.DetailSubset<K>>,
	initialFormValues: StudentNoteFormValues.DetailSubset<K>,
): StudentNoteUpdateInput {
	const inputFormValues = omitKeys(formValues, ["createdAt", "authorID", "primaryStudentIDs", "secondaryStudentIDs"]);
	const input: StudentNoteUpdateInput = { ...inputFormValues };

	if (subsetTypeHasKey(formValues, "resolvedAt") && formValues.resolvedAt === null) {
		input.clearResolvedAt = true;
	}

	if (
		subsetTypeHasKey(formValues, "primaryStudentIDs") &&
		subsetTypeHasKey(initialFormValues, "primaryStudentIDs") &&
		formValues.primaryStudentIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.primaryStudentIDs, formValues.primaryStudentIDs);
		input.addPrimaryStudentIDs = added;
		input.removePrimaryStudentIDs = removed;
	}

	if (
		subsetTypeHasKey(formValues, "secondaryStudentIDs") &&
		subsetTypeHasKey(initialFormValues, "secondaryStudentIDs") &&
		formValues.secondaryStudentIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.secondaryStudentIDs, formValues.secondaryStudentIDs);
		input.addSecondaryStudentIDs = added;
		input.removeSecondaryStudentIDs = removed;
	}

	return input;
}

export type StudentNoteFormKeyMap = {
	noteType: "noteType";
	createdAt: "createdAt";
	body: "body";
	isPinned: "isPinned";
	requiresResolution: "requiresResolution";
	isResolved: "isResolved";
	resolvedAt: "resolvedAt";
	resolutionDescription: "resolutionDescription";
	authorID: "author";
	primaryStudentIDs: "primaryStudents";
	secondaryStudentIDs: "secondaryStudents";
};

export type ToFormValuesArgs<K extends keyof StudentNoteFormValues.Detail = keyof StudentNoteFormValues.Detail> = {
	studentNote: Pick<StudentNoteDetailQuery["studentNote"], ValueOf<Pick<StudentNoteFormKeyMap, K>>>;
	fields?: { [Key in K]: true };
};

/**
 * Converts a StudentNote instance to its corresponding detail form values.
 *
 * @param studentNote The instance to convert.
 * @param fields The subset of fields to include.
 */
export function toFormValues<K extends keyof StudentNoteFormValues.Detail = keyof StudentNoteFormValues.Detail>({
	studentNote,
	fields,
}: ToFormValuesArgs<K>): StudentNoteFormValues.DetailSubset<K> {
	const formValues = {
		noteType: subsetTypeHasKey(studentNote, "noteType") ? studentNote.noteType : null,
		createdAt: subsetTypeHasKey(studentNote, "createdAt") ? studentNote.createdAt : null,
		body: subsetTypeHasKey(studentNote, "body") ? studentNote.body : "",
		isPinned: subsetTypeHasKey(studentNote, "isPinned") ? studentNote.isPinned : false,
		requiresResolution: subsetTypeHasKey(studentNote, "requiresResolution") ? studentNote.requiresResolution : false,
		isResolved: subsetTypeHasKey(studentNote, "isResolved") ? studentNote.isResolved : false,
		resolvedAt: subsetTypeHasKey(studentNote, "resolvedAt") ? studentNote.resolvedAt : null,
		resolutionDescription: subsetTypeHasKey(studentNote, "resolutionDescription")
			? studentNote.resolutionDescription
			: "",
		authorID: subsetTypeHasKey(studentNote, "author") ? studentNote.author?.id ?? null : null,
		primaryStudentIDs: subsetTypeHasKey(studentNote, "primaryStudents")
			? studentNote.primaryStudents.map((e) => e.id)
			: [],
		secondaryStudentIDs: subsetTypeHasKey(studentNote, "secondaryStudents")
			? studentNote.secondaryStudents.map((e) => e.id)
			: [],
	};
	return fields ? pickKeys(formValues, getObjectKeys(fields)) : formValues;
}
